import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { keyframes, styled } from '@mui/material/styles';
import TipsAndUpdatesRoundedIcon from '@mui/icons-material/TipsAndUpdatesRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import CommonButton from '@src/new-components/Button';
import { COLOR } from '@src/styles/color';
import {
  getDataSessionStorage,
  setDataSessionStorage,
} from '@src/utils/sessionStorage';

const ATTENTION_FIRST_DELAY_MS = 1500;
const ATTENTION_INTERVAL_MS = 6000;
const ATTENTION_MAX_RUNS = 3;
const ATTENTION_DURATION = '1.2s';

const shine = keyframes`
  from { left: -60%; }
  to { left: 130%; }
`;

const bulbGlow = keyframes`
  0%, 100% { filter: none; transform: scale(1); }
  40% {
    filter: drop-shadow(0 0 4px ${COLOR.white});
    transform: scale(1.2) rotate(-10deg);
  }
`;

const StyledFindYourPlanButton = styled('div')({
  position: 'fixed',
  right: 24,
  bottom: 24,
  zIndex: 1000,

  '.find-plan-button': { position: 'relative', overflow: 'hidden' },
  '.find-plan-button::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-60%',
    width: '40%',
    height: '100%',
    background: `linear-gradient(110deg, transparent, ${COLOR.white} 50%, transparent)`,
    opacity: 0.7,
    pointerEvents: 'none',
  },
  '&.attention .find-plan-button::after': {
    animation: `${shine} ${ATTENTION_DURATION} ease-in-out`,
  },
  '&.attention .find-plan-bulb': {
    animation: `${bulbGlow} ${ATTENTION_DURATION} ease-in-out`,
  },

  '@media (prefers-reduced-motion: reduce)': {
    '&, & *, & *::after': { animation: 'none !important' },
  },
});

const BUTTON_STYLE = {
  height: '40px',
  padding: '8px 16px',
  borderRadius: '8px',
  boxShadow: `0 4px 16px ${COLOR.black[16]}`,
};

const ICON_SX = { fontSize: 20, color: COLOR.darkBlue };

// Pinned bottom-right on desktop pricing; a few shine runs draw the eye, then it stays still.
const FindYourPlanButton = ({ onClick }) => {
  const { t } = useTranslation();
  const userId = useSelector((state) => state.auth.user?.id);
  const clickedKey = `find_your_plan_clicked_${userId}`;
  const [clicked, setClicked] = useState(() =>
    Boolean(getDataSessionStorage(clickedKey)),
  );
  const [attention, setAttention] = useState(false);

  useEffect(() => {
    if (clicked) return undefined;

    let runs = 0;
    let timer;
    const run = () => {
      setAttention(true);
      runs += 1;
      if (runs < ATTENTION_MAX_RUNS)
        timer = setTimeout(run, ATTENTION_INTERVAL_MS);
    };
    timer = setTimeout(run, ATTENTION_FIRST_DELAY_MS);
    return () => clearTimeout(timer);
  }, [clicked]);

  const handleClick = () => {
    setDataSessionStorage(clickedKey, '1');
    setClicked(true);
    setAttention(false);
    onClick();
  };

  return (
    <StyledFindYourPlanButton
      className={attention ? 'attention' : undefined}
      // The shine on the button ends last; clearing the class lets the next run replay it.
      onAnimationEnd={(event) => {
        if (event.animationName.includes(shine.name)) setAttention(false);
      }}
    >
      <CommonButton
        className="find-plan-button"
        startIcon={
          <TipsAndUpdatesRoundedIcon className="find-plan-bulb" sx={ICON_SX} />
        }
        endIcon={<KeyboardArrowRightRoundedIcon sx={ICON_SX} />}
        customStyle={BUTTON_STYLE}
        textProps={{ variantText: 'body-14-semibold', color: COLOR.darkBlue }}
        onClick={handleClick}
      >
        {t('findYourPlan')}
      </CommonButton>
    </StyledFindYourPlanButton>
  );
};

export default FindYourPlanButton;
